import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { GearSix, ShieldCheck, Users, User } from '@phosphor-icons/react';
import { WatchHowLink } from '../components/WatchHowLink';
import GmailConnect from '../components/GmailConnect';

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  manager: 'bg-blue-100 text-blue-700 border-blue-200',
  user: 'bg-gray-100 text-gray-600 border-gray-200',
};

// Inline editor for a single reusable dropdown list
function ListEditor({ list, onSave, onCancel }) {
  const [name, setName] = useState(list.name);
  const [optionsText, setOptionsText] = useState((list.options || []).join('\n'));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2" data-testid={`list-editor-${list.list_id}`}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="sm:col-span-4 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white"
        data-testid="list-edit-name"
      />
      <textarea
        value={optionsText}
        onChange={(e) => setOptionsText(e.target.value)}
        rows={3}
        className="sm:col-span-6 px-2 py-1 text-[12px] border border-[#B0B0B0] bg-white font-mono leading-tight"
        data-testid="list-edit-options"
      />
      <button
        onClick={() => onSave(name.trim(), optionsText.split('\n').map(s => s.trim()).filter(Boolean))}
        className="sm:col-span-1 h-[28px] text-[11px] bg-[#0B5394] text-white hover:bg-[#073763] rounded-sm"
        data-testid="list-edit-save"
      >Save</button>
      <button
        onClick={onCancel}
        className="sm:col-span-1 h-[28px] text-[11px] border border-[#B0B0B0] hover:bg-[#F0F0F0] rounded-sm"
        data-testid="list-edit-cancel"
      >Cancel</button>
    </div>
  );
}

export default function SettingsPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [userFields, setUserFields] = useState([]);
  const [fieldsSaving, setFieldsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [dropdownLists, setDropdownLists] = useState([]);
  const [newListName, setNewListName] = useState('');
  const [newListOptions, setNewListOptions] = useState('');
  const [savingList, setSavingList] = useState(false);
  const [editingListId, setEditingListId] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    const promises = [];
    if (isAdmin || currentUser?.role === 'manager') {
      promises.push(api.get('/users').then(({ data }) => setUsers(data)));
    }
    // Field config + dropdown lists: every account manages its own
    promises.push(api.get('/settings/user-fields').then(({ data }) => setUserFields(data.fields || [])));
    promises.push(api.get('/dropdown-lists').then(({ data }) => setDropdownLists(data || [])));
    api.get('/version').then(({ data }) => setAppVersion(data.version || '')).catch(() => {});
    Promise.all(promises).catch(console.error).finally(() => setLoading(false));
  }, [isAdmin, currentUser?.role]);

  const reloadDropdownLists = async () => {
    const { data } = await api.get('/dropdown-lists');
    setDropdownLists(data || []);
  };

  const createDropdownList = async () => {
    if (!newListName.trim()) return;
    setSavingList(true);
    try {
      const opts = newListOptions.split('\n').map(s => s.trim()).filter(Boolean);
      await api.post('/dropdown-lists', { name: newListName.trim(), options: opts });
      setNewListName('');
      setNewListOptions('');
      await reloadDropdownLists();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Could not create list');
    } finally {
      setSavingList(false);
    }
  };

  const updateDropdownList = async (listId, name, options) => {
    try {
      await api.put(`/dropdown-lists/${listId}`, { name, options });
      await reloadDropdownLists();
      setEditingListId(null);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Could not update list');
    }
  };

  const deleteDropdownList = async (listId) => {
    if (!window.confirm('Delete this drop-down list?')) return;
    try {
      await api.delete(`/dropdown-lists/${listId}`);
      await reloadDropdownLists();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Could not delete list');
    }
  };

  const addUserField = () => {
    if (userFields.length >= 100) return;
    // Find next free userN id
    let i = 1;
    const ids = new Set(userFields.map(f => f.id));
    while (ids.has(`user${i}`)) i++;
    setUserFields([...userFields, { id: `user${i}`, label: `User ${i}`, type: 'text', options: [], list_id: null }]);
  };

  const removeUserField = (idx) => {
    if (userFields.length <= 1) return;
    if (!window.confirm(`Remove field "${userFields[idx].label}"? Existing contact data for this field will become orphaned (not deleted).`)) return;
    setUserFields(userFields.filter((_, i) => i !== idx));
  };

  const updateFieldListId = (idx, list_id) => {
    setUserFields(prev => prev.map((f, i) => i === idx ? { ...f, list_id: list_id || null } : f));
  };

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const { data } = await api.get('/stats/internal');
      setStats(data);
    } catch (err) {
      setMessage('Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    setUpdating(userId);
    try {
      await api.put(`/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      setMessage(`Role updated to ${newRole}`);
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setMessage('Failed to update role');
    } finally {
      setUpdating(null);
    }
  };

  const updateFieldLabel = (idx, newLabel) => {
    setUserFields(prev => prev.map((f, i) => i === idx ? { ...f, label: newLabel } : f));
  };

  const updateFieldType = (idx, newType) => {
    setUserFields(prev => prev.map((f, i) => {
      if (i !== idx) return f;
      // Reset options when switching away from dropdown
      return { ...f, type: newType, options: newType === 'dropdown' ? (f.options || []) : [] };
    }));
  };

  const updateFieldOptions = (idx, optionsText) => {
    // Options are stored as an array but edited as newline-separated text
    const opts = optionsText.split('\n').map(s => s.trim()).filter(Boolean);
    setUserFields(prev => prev.map((f, i) => i === idx ? { ...f, options: opts } : f));
  };

  const exportFieldConfig = () => {
    const payload = {
      type: 'salescrunch_user_fields',
      version: 1,
      exported_at: new Date().toISOString(),
      fields: userFields.map(f => ({
        id: f.id,
        label: f.label,
        type: f.type || 'text',
        options: f.options || [],
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `salescrunch_userfields_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importFieldConfig = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMessage('');
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (parsed.type !== 'salescrunch_user_fields') {
        throw new Error('Not a SalesCrunch user-field config file.');
      }
      if (!Array.isArray(parsed.fields) || parsed.fields.length !== 10) {
        throw new Error('Config must contain exactly 10 fields.');
      }
      const VALID = ['text', 'dropdown', 'checkbox', 'date', 'number'];
      const clean = parsed.fields.map((f, i) => {
        const type = VALID.includes(f.type) ? f.type : 'text';
        return {
          id: `user${i + 1}`,
          label: String(f.label || `User ${i + 1}`).trim(),
          type,
          options: type === 'dropdown' && Array.isArray(f.options) ? f.options.map(String) : [],
        };
      });
      if (!window.confirm(`Import ${clean.length} field configuration? This will replace your current user field labels and types.`)) {
        event.target.value = '';
        return;
      }
      const { data } = await api.put('/settings/user-fields', { fields: clean });
      setUserFields(data.fields);
      setMessage(`Imported ${data.fields.length} user fields from ${file.name}`);
    } catch (err) {
      setMessage(`Import failed: ${err.message || err}`);
    } finally {
      event.target.value = ''; // reset input so same file can be re-picked
    }
  };

  const exportWorkspace = async () => {
    try {
      const { data } = await api.get('/admin/workspace/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `salescrunch_workspace_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage(`Exported workspace: ${data.counts?.user_fields ?? 0} fields · ${data.counts?.templates ?? 0} templates · ${data.counts?.groups ?? 0} groups`);
    } catch (err) {
      setMessage('Workspace export failed.');
    }
  };

  const importWorkspace = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setMessage('');
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (parsed.type !== 'salescrunch_workspace_backup') {
        throw new Error('Not a SalesCrunch workspace backup file.');
      }
      const summary = parsed.counts || {};
      const confirmMsg = `Import workspace backup?\n\n` +
        `• ${summary.user_fields ?? 0} user fields (will replace yours)\n` +
        `• ${summary.templates ?? 0} templates (new ones added, duplicates skipped by name)\n` +
        `• ${summary.groups ?? 0} groups (new ones added, duplicates skipped by name)\n\n` +
        `Contacts, activities, and users are never touched.`;
      if (!window.confirm(confirmMsg)) {
        event.target.value = '';
        return;
      }
      const { data } = await api.post('/admin/workspace/import', parsed);
      // Reload user fields from server to reflect any changes
      const { data: ufRes } = await api.get('/settings/user-fields');
      setUserFields(ufRes.fields || []);
      const c = data.created || {};
      const s = data.skipped || {};
      setMessage(
        `Workspace imported: ${c.user_fields || 0} fields · ${c.templates || 0} new templates (${s.templates || 0} skipped) · ${c.groups || 0} new groups (${s.groups || 0} skipped)`
      );
    } catch (err) {
      setMessage(`Workspace import failed: ${err.response?.data?.detail || err.message || err}`);
    } finally {
      event.target.value = '';
    }
  };

  const saveUserFields = async () => {
    setFieldsSaving(true);
    try {
      await api.put('/settings/user-fields', { fields: userFields });
      setMessage('User fields saved');
      setTimeout(() => setMessage(''), 2000);
    } catch (err) {
      setMessage('Failed to save');
    } finally {
      setFieldsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0B5394] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="settings-page">
      {/* Toolbar */}
      <div className="bg-gradient-to-b from-[#E8E8E8] to-[#D0D0D0] border-b border-[#999] px-3 py-1.5 flex items-center gap-2 shrink-0">
        <GearSix size={16} weight="bold" className="text-[#0B5394]" />
        <span className="text-[13px] font-bold text-[#333]">Settings</span>
        <div className="flex-1" />
        {message && <span className="text-[11px] font-medium text-green-700">{message}</span>}
      </div>

      <div className="flex-1 overflow-auto p-2 bg-[#E8E8E8]">
        {/* Your Profile */}
        <div className="bg-[#F5F5F0] border border-[#999] mb-2">
          <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
            <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Your Profile</h3>
          </div>
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0B5394] flex items-center justify-center text-white font-bold text-sm">
              {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="text-[13px] font-bold text-[#333]">{currentUser?.name}</p>
              <p className="text-[11px] text-[#666]">{currentUser?.email} &middot; <span className="capitalize">{currentUser?.role}</span></p>
            </div>
          </div>
        </div>

        {/* Subscription */}
        {currentUser?.stripe_customer_id && (
          <div className="bg-[#F5F5F0] border border-[#999] mb-2" data-testid="subscription-section">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Subscription</h3>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[12px] text-[#333]">
                  <span className="font-bold">SalesCrunchCRM Monthly</span> &middot; $30/user/month
                  {currentUser?.subscription_active === false && (
                    <span className="ml-2 text-red-600 font-bold">(inactive)</span>
                  )}
                </p>
                <p className="text-[11px] text-[#666] mt-0.5">
                  Update your card, view invoices, or cancel anytime via Stripe&apos;s secure portal.
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    setMessage('Opening billing portal…');
                    const { data } = await api.post('/billing/portal', { return_url: window.location.origin + '/settings' });
                    window.location.href = data.url;
                  } catch (err) {
                    setMessage(err.response?.data?.detail || 'Could not open billing portal.');
                    setTimeout(() => setMessage(''), 4000);
                  }
                }}
                data-testid="manage-subscription-btn"
                className="text-[12px] bg-[#0B5394] hover:bg-[#094180] text-white font-semibold px-4 py-1.5 rounded shadow-sm shrink-0"
              >
                Manage subscription
              </button>
            </div>
          </div>
        )}

        {/* Gmail OAuth Integration */}
        <GmailConnect />

        {/* User Management */}
        {(isAdmin || currentUser?.role === 'manager') && (
          <div className="bg-[#F5F5F0] border border-[#999] mb-2" data-testid="user-management-section">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">User Management</h3>
            </div>
            <table className="w-full text-[11px]" data-testid="users-table">
              <thead>
                <tr className="bg-[#E8E8E8] border-b border-[#999]">
                  <th className="px-3 py-2 text-left font-semibold text-[#333]">User</th>
                  <th className="px-3 py-2 text-left font-semibold text-[#333]">Email</th>
                  <th className="px-3 py-2 text-left font-semibold text-[#333]">Role</th>
                  {isAdmin && <th className="px-3 py-2 text-left font-semibold text-[#333]">Change Role</th>}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-b border-[#DDD] hover:bg-[#E8E8E8]" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-3 py-2 font-medium text-[#333]">{u.name}</td>
                    <td className="px-3 py-2 text-[#666]">{u.email}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-medium rounded-sm border capitalize ${ROLE_COLORS[u.role] || ROLE_COLORS.user}`}>
                        {u.role}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2">
                        {u.user_id === currentUser?.user_id ? (
                          <span className="text-[10px] text-[#999]">Current user</span>
                        ) : (
                          <select
                            value={u.role}
                            onChange={(e) => updateRole(u.user_id, e.target.value)}
                            disabled={updating === u.user_id}
                            className="h-[26px] px-2 text-[11px] border border-[#B0B0B0] bg-white cursor-pointer"
                            data-testid={`role-select-${u.user_id}`}
                          >
                            <option value="admin">Admin</option>
                            <option value="manager">Manager</option>
                            <option value="user">User</option>
                          </select>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Reusable Drop-down Lists (Phase B) — every account manages its own */}
        {currentUser && (
          <div className="bg-[#F5F5F0] border border-[#999]" data-testid="dropdown-lists-section">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Reusable Drop-down Lists</h3>
              <WatchHowLink slug="custom-fields" className="ml-2" />
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-[#EEF4FB] border border-[#B8D0E8] text-[#0B5394] text-[11px] p-3 rounded-sm">
                <p className="font-bold mb-1">Define a list once, reuse it everywhere</p>
                <p>Create lists like <i>"Lead Sources"</i> or <i>"Industries"</i> here, then point any user field at them. Update the list — every field using it updates instantly.</p>
              </div>

              {/* Create new list */}
              <div className="border border-[#D0D0D0] bg-white p-3 grid grid-cols-1 sm:grid-cols-12 gap-2" data-testid="create-list-form">
                <input
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="List name (e.g. Industries)"
                  className="sm:col-span-4 h-[28px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                  data-testid="new-list-name"
                />
                <textarea
                  value={newListOptions}
                  onChange={(e) => setNewListOptions(e.target.value)}
                  placeholder="Options, one per line"
                  rows={3}
                  className="sm:col-span-6 px-2 py-1 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394] font-mono leading-tight"
                  data-testid="new-list-options"
                />
                <button
                  onClick={createDropdownList}
                  disabled={!newListName.trim() || savingList}
                  className="sm:col-span-2 h-[28px] text-[11px] bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 rounded-sm"
                  data-testid="create-list-btn"
                >
                  {savingList ? '...' : '+ Add List'}
                </button>
              </div>

              {/* Existing lists */}
              {dropdownLists.length === 0 ? (
                <p className="text-[12px] text-[#999] text-center py-2">No reusable lists yet. Create one above.</p>
              ) : (
                <div className="space-y-2">
                  {dropdownLists.map(l => {
                    const isEditing = editingListId === l.list_id;
                    return (
                      <div key={l.list_id} className="border border-[#D0D0D0] bg-white p-2" data-testid={`dropdown-list-${l.list_id}`}>
                        {isEditing ? (
                          <ListEditor
                            list={l}
                            onSave={(name, options) => updateDropdownList(l.list_id, name, options)}
                            onCancel={() => setEditingListId(null)}
                          />
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[12px] font-semibold text-[#333]" data-testid={`dropdown-list-name-${l.list_id}`}>{l.name}</span>
                            <span className="text-[10px] text-[#999]">({l.options.length} options)</span>
                            <span className="flex-1 text-[11px] text-[#666] truncate" title={l.options.join(', ')}>
                              {l.options.slice(0, 5).join(' · ')}{l.options.length > 5 ? ` …+${l.options.length - 5}` : ''}
                            </span>
                            <button
                              onClick={() => setEditingListId(l.list_id)}
                              className="text-[11px] px-2 py-0.5 border border-[#B0B0B0] hover:bg-[#F0F0F0] rounded-sm"
                              data-testid={`edit-list-${l.list_id}`}
                            >Edit</button>
                            <button
                              onClick={() => deleteDropdownList(l.list_id)}
                              className="text-[11px] px-2 py-0.5 border border-[#E0B0B0] text-[#C0392B] hover:bg-red-50 rounded-sm"
                              data-testid={`delete-list-${l.list_id}`}
                            >Delete</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Custom User Fields — every account manages its own */}
        {currentUser && (
          <div className="bg-[#F5F5F0] border border-[#999]" data-testid="user-fields-section">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999] flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Custom User Fields</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportFieldConfig}
                  className="px-2 py-1 text-[10px] font-medium bg-white border border-[#B0B0B0] text-[#333] hover:bg-[#F0F0F0] rounded-sm"
                  data-testid="export-user-fields-btn"
                  title="Download your field config as a JSON file"
                >
                  Export Config
                </button>
                <label
                  className="px-2 py-1 text-[10px] font-medium bg-white border border-[#B0B0B0] text-[#333] hover:bg-[#F0F0F0] rounded-sm cursor-pointer"
                  data-testid="import-user-fields-label"
                  title="Upload a previously-exported JSON file"
                >
                  Import Config
                  <input
                    type="file"
                    accept="application/json,.json"
                    onChange={importFieldConfig}
                    className="hidden"
                    data-testid="import-user-fields-input"
                  />
                </label>
                <button
                  onClick={saveUserFields}
                  disabled={fieldsSaving}
                  className="px-3 py-1 text-[10px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 rounded-sm"
                  data-testid="save-user-fields-btn"
                >
                  {fieldsSaving ? '...' : 'Save Fields'}
                </button>
              </div>
            </div>
            <div className="p-4">
              {/* Instructions callout */}
              <div className="bg-[#EEF4FB] border border-[#B8D0E8] text-[#0B5394] text-[11px] p-3 mb-4 rounded-sm" data-testid="user-fields-help">
                <p className="font-bold mb-1">How to customize your user fields</p>
                <ol className="list-decimal pl-4 space-y-0.5">
                  <li>Pick a <b>label</b> that matches your business (e.g. <i>Lead Source</i>, <i>Lead Strength</i>, <i>Renewal Date</i>).</li>
                  <li>Pick a <b>type</b>: Text (free typing), Dropdown (fixed list), Checkbox (Yes/No), Date, or Number.</li>
                  <li>For <b>Dropdown</b>, enter each option on its own line in the Options box (e.g. <i>Hot, Mild, Cool, Cold</i>).</li>
                  <li>Hit <b>Save User Fields</b> — your labels apply to every contact instantly.</li>
                </ol>
                <p className="mt-2">Tip: you can also rename fields directly on a contact card — click the ✎ pencil next to any user-field label.</p>
                <p className="mt-1">Setting up multiple databases? Use <b>Export Config</b> to save your labels/types as a JSON file, then <b>Import Config</b> into the other install.</p>
              </div>
              <div className="space-y-3">
                {userFields.map((field, idx) => (
                  <div
                    key={field.id}
                    className="border border-[#D0D0D0] bg-white p-2 grid grid-cols-1 sm:grid-cols-12 gap-2 items-start"
                    data-testid={`user-field-config-${field.id}`}
                  >
                    <span className="sm:col-span-1 text-[11px] text-[#999] font-mono self-center truncate" title={field.id}>{field.id}</span>
                    <input
                      value={field.label}
                      onChange={(e) => updateFieldLabel(idx, e.target.value)}
                      autoComplete="one-time-code"
                      placeholder="Label"
                      className="sm:col-span-3 h-[26px] px-2 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                      data-testid={`user-field-label-${field.id}`}
                    />
                    <select
                      value={field.type || 'text'}
                      onChange={(e) => updateFieldType(idx, e.target.value)}
                      className="sm:col-span-2 h-[26px] px-1 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                      data-testid={`user-field-type-${field.id}`}
                    >
                      <option value="text">Text</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="date">Date</option>
                      <option value="number">Number</option>
                    </select>
                    {field.type === 'dropdown' ? (
                      <>
                        <select
                          value={field.list_id || ''}
                          onChange={(e) => updateFieldListId(idx, e.target.value)}
                          className="sm:col-span-3 h-[26px] px-1 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394]"
                          data-testid={`user-field-list-${field.id}`}
                          title="Use a reusable list (defined below) — or pick 'Inline' to type options"
                        >
                          <option value="">— Inline options —</option>
                          {dropdownLists.map(l => (
                            <option key={l.list_id} value={l.list_id}>{l.name}</option>
                          ))}
                        </select>
                        {!field.list_id ? (
                          <textarea
                            value={(field.options || []).join('\n')}
                            onChange={(e) => updateFieldOptions(idx, e.target.value)}
                            placeholder="One option per line"
                            rows={Math.max(2, (field.options || []).length)}
                            className="sm:col-span-2 px-2 py-1 text-[12px] border border-[#B0B0B0] bg-white focus:outline-none focus:border-[#0B5394] font-mono leading-tight"
                            data-testid={`user-field-options-${field.id}`}
                          />
                        ) : (
                          <span className="sm:col-span-2 text-[11px] text-[#0B5394] italic self-center">
                            ↑ uses list "{dropdownLists.find(l => l.list_id === field.list_id)?.name || '?'}"
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="sm:col-span-5 text-[11px] text-[#999] italic self-center">
                        {field.type === 'text' && 'Free text field'}
                        {field.type === 'checkbox' && 'Yes / No checkbox'}
                        {field.type === 'date' && 'Calendar date picker'}
                        {field.type === 'number' && 'Numeric-only input'}
                      </span>
                    )}
                    <button
                      onClick={() => removeUserField(idx)}
                      className="sm:col-span-1 h-[26px] px-1.5 text-[11px] text-[#C0392B] hover:bg-red-50 border border-[#E0B0B0] rounded-sm self-center"
                      title="Remove this field"
                      data-testid={`user-field-remove-${field.id}`}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={addUserField}
                  disabled={userFields.length >= 100}
                  className="w-full h-[28px] text-[12px] border border-dashed border-[#999] hover:border-[#0B5394] hover:text-[#0B5394] text-[#666] disabled:opacity-50"
                  data-testid="add-user-field-btn"
                >
                  + Add another field ({userFields.length}/100)
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Workspace Backup — admin only */}
        {isAdmin && (
          <div className="bg-[#F5F5F0] border border-[#999] mt-2" data-testid="workspace-backup-section">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">Workspace Backup</h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="bg-[#EEF4FB] border border-[#B8D0E8] text-[#0B5394] text-[11px] p-3 rounded-sm">
                <p className="font-bold mb-1">One-click admin config backup</p>
                <p>Export everything an admin sets up — user fields, templates, and group definitions — as a single JSON file. Restore it to a fresh install in seconds.</p>
                <p className="mt-2"><b>Not included:</b> contacts, activities, deals, users. Those are your <i>data</i>, not config.</p>
                <p className="mt-1"><b>On import:</b> user fields replace existing ones; templates and groups merge (duplicates skipped by name).</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={exportWorkspace}
                  className="px-3 py-1.5 text-[12px] font-medium bg-[#0B5394] text-white hover:bg-[#073763] rounded-sm"
                  data-testid="export-workspace-btn"
                >
                  📥 Export Workspace
                </button>
                <label
                  className="px-3 py-1.5 text-[12px] font-medium bg-white border border-[#B0B0B0] text-[#333] hover:bg-[#F0F0F0] rounded-sm cursor-pointer"
                  data-testid="import-workspace-label"
                >
                  📤 Import Workspace
                  <input
                    type="file"
                    accept="application/json,.json"
                    onChange={importWorkspace}
                    className="hidden"
                    data-testid="import-workspace-input"
                  />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* System Stats — admin only */}
        {isAdmin && (
          <div className="bg-[#F5F5F0] border border-[#999] mt-2" data-testid="system-stats-section">
            <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999] flex items-center justify-between">
              <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">System Stats (internal)</h3>
              <button
                onClick={loadStats}
                disabled={statsLoading}
                className="text-[11px] px-2 py-1 bg-[#0B5394] text-white hover:bg-[#073763] disabled:opacity-50 rounded-sm"
                data-testid="load-stats-btn"
              >
                {statsLoading ? 'Loading...' : stats ? 'Refresh' : 'Load Stats'}
              </button>
            </div>
            {stats ? (
              <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4 text-[11px]">
                <div data-testid="stats-collections">
                  <h4 className="font-bold text-[#333] mb-1">Collection Counts</h4>
                  <table className="w-full">
                    <tbody>
                      {Object.entries(stats.collection_counts || {}).map(([k, v]) => (
                        <tr key={k} className="border-b border-[#E0E0E0]">
                          <td className="py-1 text-[#555]">{k}</td>
                          <td className="py-1 text-right font-mono text-[#0B5394]">{v?.toLocaleString() ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h4 className="font-bold text-[#333] mb-1">Database</h4>
                  <table className="w-full">
                    <tbody>
                      {Object.entries(stats.db_stats || {}).map(([k, v]) => (
                        <tr key={k} className="border-b border-[#E0E0E0]">
                          <td className="py-1 text-[#555]">{k.replace(/_/g, ' ')}</td>
                          <td className="py-1 text-right font-mono text-[#0B5394]">{typeof v === 'number' ? v.toLocaleString() : String(v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <h4 className="font-bold text-[#333] mt-3 mb-1">Audit — edits (last 24h)</h4>
                  <p className="text-[18px] font-bold text-[#0B5394] font-mono" data-testid="stats-recent-edits">
                    {(stats.recent_edits_24h || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-[#333] mb-1">Contact Status Breakdown</h4>
                  <table className="w-full">
                    <tbody>
                      {Object.entries(stats.contact_status_breakdown || {}).map(([k, v]) => (
                        <tr key={k} className="border-b border-[#E0E0E0]">
                          <td className="py-1 text-[#555] capitalize">{k}</td>
                          <td className="py-1 text-right font-mono text-[#0B5394]">{v.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h4 className="font-bold text-[#333] mb-1">Activity Type Breakdown</h4>
                  <table className="w-full">
                    <tbody>
                      {Object.entries(stats.activity_type_breakdown || {}).map(([k, v]) => (
                        <tr key={k} className="border-b border-[#E0E0E0]">
                          <td className="py-1 text-[#555] capitalize">{k}</td>
                          <td className="py-1 text-right font-mono text-[#0B5394]">{v.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="lg:col-span-2 text-[10px] text-[#999] mt-1">
                  Generated: {new Date(stats.generated_at).toLocaleString()}
                </p>
              </div>
            ) : (
              <div className="p-4 text-center text-[11px] text-[#666]">
                Click "Load Stats" to see database health, row counts, and activity breakdown.
              </div>
            )}
          </div>
        )}

        {/* About / Version */}
        <div className="bg-[#F5F5F0] border border-[#999] mt-2" data-testid="about-section">
          <div className="px-3 py-2 bg-[#E0E0E0] border-b border-[#999]">
            <h3 className="text-[12px] font-bold text-[#CC6600] uppercase tracking-wide">About</h3>
          </div>
          <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <p className="text-[13px] font-bold text-[#333]">SalesCrunchCRM</p>
              <p className="text-[11px] text-[#666]">ACT! 4.0 reimagined for modern teams.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-[#666]">Version:</span>
              <span
                className="inline-flex items-center px-2 py-0.5 bg-[#0B5394] text-white text-[11px] font-mono rounded-sm"
                data-testid="app-version-badge"
              >
                v{appVersion || '…'}
              </span>
              <button
                onClick={async () => {
                  try {
                    localStorage.removeItem('app_version');
                    if ('caches' in window) {
                      const names = await caches.keys();
                      await Promise.all(names.map(n => caches.delete(n)));
                    }
                  } catch {}
                  window.location.reload();
                }}
                className="text-[11px] px-2 py-1 bg-white border border-[#B0B0B0] text-[#333] hover:bg-[#E8E8E8] rounded-sm"
                data-testid="force-refresh-btn"
                title="Clear local cache and reload"
              >
                Force refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
